import axios from 'axios';
import { getAuthHeaders } from '../utils/authUtils';
import { ReturnCodes } from '../utils/constants';

class UserService {
    /**
     * Determine whether to use enhanced user experience API (v2) or classic API (v1)
     * @returns {boolean} True if enhanced user experience should be used
     */
    shouldUseEnhancedUser() {
        return process.env.REACT_APP_USE_ENHANCED_USER === 'true' || localStorage.getItem('userExperience') === 'new';
    }

    /**
     * Get all users from the system
     * @returns {Promise} Promise resolving to array of users
     */
    async getAllUsers(email, auth) {
        return new Promise(async (resolve, reject) => {
            try {
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = `${apiBaseUrl}/api/v2/users`;

                await axios
                    .get(apiEndpoint, {
                        headers: getAuthHeaders(),
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(ReturnCodes.UNABLE_TO_GET_USERS);
                    });
            } catch (error) {
                reject(ReturnCodes.UNABLE_TO_GET_USERS);
            }
        });
    }

    /**
     * Login user with email and password
     * @param {string} email - User's email
     * @param {string} password - User's password
     * @returns {Promise} Promise resolving to login response
     */
    async login(email, password) {
        return new Promise(async (resolve, reject) => {
            try {
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = `${apiBaseUrl}/api/v2/login`;

                await axios
                    .post(apiEndpoint, {
                        email: email,
                        password: password,
                    })
                    .then((response) => {
                        resolve(response);
                    })
                    .catch((error) => {
                        reject(ReturnCodes.FAILED);
                    });
            } catch (error) {
                reject(ReturnCodes.FAILED);
            }
        });
    }

    /**
     * Handle API errors
     * @param {Error} error - Error object from API call
     * @returns {ReturnCodes} Appropriate return code for the error
     */
    handleError(error) {
        if (error instanceof Error && error.response && error.response.data) {
            const errorMessage = error.response.data.message || '';

            if (errorMessage.includes('Duplicate entry') || errorMessage.includes('already exists')) {
                console.log('Duplicate entry error detected.');
                return ReturnCodes.DUPLICATE_EMAIL;
            } else {
                console.log('Other error detected:', errorMessage);
                return ReturnCodes.FAILED;
            }
        } else {
            console.log('Unknown error format:', error);
            return ReturnCodes.FAILED;
        }
    }

    /**
     * Register new user
     * @param {string} firstName - User's first name
     * @param {string} lastName - User's last name
     * @param {string} email - User's email
     * @param {string} password - User's password
     * @returns {Promise} Promise resolving to created user
     */
    async register(firstName, lastName, email, password, userType = 'user', createdBy = null, notificationEmail) {
        const authUserEmail = JSON.parse(localStorage.getItem('authUser')).email;
        const authToken = JSON.parse(localStorage.getItem('authToken'));

        const apiBaseUrl = process.env.REACT_APP_API_URL;
        const apiEndpoint = `${apiBaseUrl}/api/v2/users`;

        return axios.post(
            apiEndpoint,
            {
                firstName,
                lastName,
                email,
                password,
                userType,
                createdBy, // Add this field to the request
                notificationEmail,
            },
            {
                headers: {
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Credentials': true,
                    email: authUserEmail,
                    auth: authToken,
                },
            },
        );
    }

    /**
     * Get user by email
     * @param {string} userEmail - Email of the user to retrieve
     * @param {string} email - Current user's email for auth
     * @param {string} auth - Current user's auth token
     * @returns {Promise} Promise resolving to user data
     */
    async getUserByEmail(userEmail, email, auth) {
        return new Promise(async (resolve, reject) => {
            try {
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = `${apiBaseUrl}/api/v2/user/${userEmail}`;

                await axios
                    .get(apiEndpoint, {
                        headers: {
                            'Access-Control-Allow-Origin': '*',
                            'Access-Control-Allow-Credentials': true,
                            email: email,
                            auth: auth,
                        },
                    })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(ReturnCodes.UNABLE_TO_GET_USER);
                    });
            } catch (error) {
                reject(ReturnCodes.UNABLE_TO_GET_USER);
            }
        });
    }

    /**
     * Update an existing user
     * @param {string} userEmail - Email of the user to update
     * @param {object} userData - Updated user data
     * @param {string} email - Current user's email for auth
     * @param {string} auth - Current user's auth token
     * @returns {Promise} Promise resolving to updated user
     */
    async updateUser(userEmail, userData, email, auth) {
        const headers = getAuthHeaders(true);
        return new Promise(async (resolve, reject) => {
            try {
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = `${apiBaseUrl}/api/v2/user/${userEmail}`;

                await axios
                    .put(apiEndpoint, { userData }, { headers })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(ReturnCodes.FAILED);
                    });
            } catch (error) {
                reject(ReturnCodes.FAILED);
            }
        });
    }

    /**
     * Delete a user by email
     * @param {string} userEmail - Email of the user to delete
     * @param {string} email - Current user's email for auth
     * @param {string} auth - Current user's auth token
     * @returns {Promise} Promise resolving to deletion status
     */
    async deleteUser(userEmail, email, auth) {
        return new Promise(async (resolve, reject) => {
            try {
                const apiBaseUrl = process.env.REACT_APP_API_URL;
                const apiEndpoint = `${apiBaseUrl}/api/v2/user/${userEmail}`;
                const headers = getAuthHeaders(true);
                await axios
                    .delete(apiEndpoint, { headers })
                    .then((response) => {
                        resolve(response.data);
                    })
                    .catch((error) => {
                        reject(ReturnCodes.FAILED);
                    });
            } catch (error) {
                reject(ReturnCodes.FAILED);
            }
        });
    }

    // Change user password
    async changePassword(currentPassword, newPassword) {
        const apiBaseUrl = process.env.REACT_APP_API_URL;
        const apiEndpoint = '/api/v2/users/change-password';

        const authUser = JSON.parse(localStorage.getItem('authUser')).email;
        const authToken = JSON.parse(localStorage.getItem('authToken'));

        return axios.post(
            `${apiBaseUrl}${apiEndpoint}`,
            {
                authUser,
                currentPassword,
                newPassword,
            },
            {
                headers: {
                    email: authUser,
                    auth: authToken,
                    'Access-Control-Allow-Origin': '*',
                    'Access-Control-Allow-Credentials': true,
                },
            },
        );
    }

    /**
     * Create a new user
     * @param {object} userData - Data of the user to create
     * @param {string} adminEmail - Admin's email for auth
     * @param {string} authToken - Admin's auth token
     * @returns {Promise} Promise resolving to created user
     */
    async createUser(userData) {
        const apiBaseUrl = process.env.REACT_APP_API_URL;

        const authUser = JSON.parse(localStorage.getItem('authUser')).email;
        const authToken = JSON.parse(localStorage.getItem('authToken'));
        const endpoint = '/api/v2/users';

        return axios.post(`${apiBaseUrl}${endpoint}`, userData, {
            headers: {
                email: authUser,
                auth: authToken,
                'Access-Control-Allow-Origin': '*',
                'Access-Control-Allow-Credentials': true,
            },
        });
    }

    /**
     * Refresh current logged-in user's profile and permissions from server.
     * Updates localStorage('authUser') so role/language access changes apply without re-login.
     */
    async refreshCurrentUserProfile() {
        const authUserRaw = localStorage.getItem('authUser');
        const authTokenRaw = localStorage.getItem('authToken');

        if (!authUserRaw || !authTokenRaw) {
            return null;
        }

        let authUser;
        let authToken;
        try {
            authUser = JSON.parse(authUserRaw);
            authToken = JSON.parse(authTokenRaw);
        } catch (e) {
            return null;
        }

        const currentEmail = authUser?.email;
        if (!currentEmail || !authToken) {
            return null;
        }

        const apiBaseUrl = process.env.REACT_APP_API_URL;
        const apiEndpoint = `${apiBaseUrl}/api/v2/user/${currentEmail}`;
        const response = await axios.get(apiEndpoint, {
            headers: {
                email: currentEmail,
                auth: authToken,
            },
        });

        const serverUser = response?.data || {};
        let languageAccess = serverUser.languageAccess || {};
        if (typeof languageAccess === 'string') {
            try {
                languageAccess = JSON.parse(languageAccess);
            } catch (e) {
                languageAccess = {};
            }
        }

        const mergedUser = {
            ...authUser,
            email: serverUser.email || authUser.email || '',
            firstName: serverUser.firstName || authUser.firstName || '',
            lastName: serverUser.lastName || authUser.lastName || '',
            userType:
                serverUser.userType ||
                (typeof serverUser.userTypeEnum === 'string' ? serverUser.userTypeEnum.toLowerCase() : '') ||
                authUser.userType ||
                'user',
            roles: serverUser.roles || authUser.roles || [],
            languageAccess: languageAccess || {},
        };

        localStorage.setItem('authUser', JSON.stringify(mergedUser));

        // Keep last login timestamp active for users who stay logged in.
        try {
            await axios.post(
                `${apiBaseUrl}/api/v2/users/heartbeat`,
                {},
                {
                    headers: {
                        email: currentEmail,
                        auth: authToken,
                    },
                },
            );
        } catch (e) {
            // Non-blocking: auth profile refresh should still succeed if heartbeat fails.
        }

        window.dispatchEvent(new Event('auth-user-updated'));
        return mergedUser;
    }
}

const userServiceInstance = new UserService();
export default userServiceInstance;
